import { Collapse as BlueprintCollapse } from '@blueprintjs/core';
import * as React from 'react';

import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import {
  LocalGraph,
  QueryProvenanceGraphs,
  QueryProvenanceMetadata,
  VirtualGraph,
} from 'bones/constants/voicebox/provenance';
import stardogLogo from 'bones/design-system/assets/dataSources/stardog.svg';
import { InteractiveBadge } from 'bones/design-system/components/badge/InteractiveBadge';
import { Chip } from 'bones/design-system/components/chip/Chip';
import { TableBody } from 'bones/design-system/components/data-table/TableBody';
import { TableFooterStyle4 } from 'bones/design-system/components/data-table/TableFooter';
import { DataSourceLogo } from 'bones/design-system/components/DataSourceLogo';
import { Loading } from 'bones/design-system/components/Loading';
import { getLocalName } from 'bones/utils/getLocalName';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';
import { alphaSort } from 'bones/utils/sort/alphaSort';

const ROWS_PER_PAGE = 3;

export interface VoiceboxPanelDataSourcesProps {
  graphs: QueryProvenanceGraphs;
  isOpen: boolean;
  loading?: boolean;
  messageId: string;
  metadata: QueryProvenanceMetadata;
  model: QueryBuilderModel;
}

export const VoiceboxPanelDataSources: React.VFC<
  VoiceboxPanelDataSourcesProps
> = ({ graphs, isOpen, loading, messageId, metadata, model }) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { addPanelHistory, getLabelOrLocalName } = useVoiceboxPanelData();

  const { localGraphs, dataSources } = React.useMemo(() => {
    const localTypesByGraph: Record<
      string,
      { label: string; types: Set<string> }
    > = {};
    const virtualTypesBySource: Record<
      string,
      Record<string, Set<string>>
    > = {};
    const graphsByIri: Record<string, LocalGraph | VirtualGraph> = {};

    graphs.forEach((graph) => {
      if (!graphsByIri[graph.iri]) {
        graphsByIri[graph.iri] = graph;
      }

      if ('types' in graph) {
        if (!localTypesByGraph[graph.iri]) {
          localTypesByGraph[graph.iri] = {
            label: graph.name || getLocalName(graph.iri),
            types: new Set(),
          };
        }

        graph.types.forEach((type) => {
          localTypesByGraph[graph.iri].types.add(type);
        });
      }

      if ('mappings' in graph) {
        if (!virtualTypesBySource[graph.dataSourceIri]) {
          virtualTypesBySource[graph.dataSourceIri] = {};
        }

        graph.mappings.forEach((mapping) => {
          mapping.typeMappings.forEach((typeMapping) => {
            const column = metadata.columnsByIri[typeMapping.columnIri];

            // istanbul ignore if
            if (!column) {
              return;
            }

            if (!virtualTypesBySource[graph.dataSourceIri][column.tableIri]) {
              virtualTypesBySource[graph.dataSourceIri][column.tableIri] =
                new Set();
            }
            virtualTypesBySource[graph.dataSourceIri][column.tableIri].add(
              mapping.toType
            );
          });
        });
      }
    });

    const localGraphs = Object.entries(localTypesByGraph);
    const dataSources = Object.entries(virtualTypesBySource).sort(
      alphaSort(
        ([iri]) =>
          metadata.dataSourcesByIri[iri]?.name ||
          /* istanbul ignore next */ getLocalName(iri)
      )
    );

    return { localGraphs, dataSources };
  }, [graphs, metadata]);

  const firstSource = React.useMemo(
    () =>
      localGraphs.length
        ? ''
        : dataSources[0]?.[0] || /* istanbul ignore next */ '',
    [dataSources, localGraphs]
  );

  const didFirstSourceChange = useDidValueChange(firstSource);

  const [selectedSource, setSelectedSource] = React.useState(firstSource);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    if (didFirstSourceChange && !loading) {
      setSelectedSource(firstSource);
      setPage(1);
    }
  }, [didFirstSourceChange, firstSource, loading]);

  const localPages = Math.ceil(localGraphs.length / ROWS_PER_PAGE);

  const selectedDataSource = dataSources.find(
    (dataSource) => dataSource[0] === selectedSource
  );
  const virtualPages = Math.ceil(
    Object.keys(selectedDataSource?.[1] || {}).length / ROWS_PER_PAGE
  );

  return (
    <BlueprintCollapse
      className="vb-voicebox-panel-data-sources-container"
      isOpen={isOpen}
    >
      {loading ? (
        /* istanbul ignore next */
        <Loading size="lg" />
      ) : (
        <div className="vb-voicebox-panel-data-sources">
          <div className="vb-voicebox-panel-data-sources-header">
            {localGraphs.length ? (
              <Chip
                avatarProps={{ image: <img alt="stardog" src={stardogLogo} /> }}
                onClick={() => {
                  setSelectedSource('');
                  setPage(1);
                }}
                selected={selectedSource === ''}
                size="xl"
                testId={testIds.panel.dataSources.source('stardog', -1)}
                value="Stardog"
              />
            ) : null}
            {dataSources.map(([dataSourceIri], dataSourceIdx) => (
              <Chip
                key={dataSourceIri}
                avatarProps={{
                  image: (
                    <DataSourceLogo
                      iri={
                        metadata.dataSourcesByIri[dataSourceIri]?.platform ||
                        /* istanbul ignore next */ ''
                      }
                    />
                  ),
                }}
                onClick={() => {
                  setSelectedSource(dataSourceIri);
                  setPage(1);
                }}
                selected={selectedSource === dataSourceIri}
                size="xl"
                testId={testIds.panel.dataSources.source(
                  dataSourceIri,
                  dataSourceIdx
                )}
                value={
                  metadata.dataSourcesByIri[dataSourceIri]?.name ||
                  /* istanbul ignore next */ getLocalName(dataSourceIri)
                }
              />
            ))}
          </div>

          <div className="vb-voicebox-panel-data-sources-table">
            {!selectedSource && localGraphs.length ? (
              <div className="vb-data-table">
                <TableBody
                  body={localGraphs
                    .slice(ROWS_PER_PAGE * (page - 1), ROWS_PER_PAGE * page)
                    .map(([graphIri, { label, types }], graphIdx) => (
                      <React.Fragment key={graphIri}>
                        <td>{label}</td>
                        <td>
                          <div className="vb-voicebox-panel-data-sources-types">
                            {Array.from(types)
                              .filter((type) => model.classesByIri[type])
                              .map((type, typeIdx) => (
                                <InteractiveBadge
                                  key={`${graphIri}-${type}`}
                                  colorId={model.classesByIri[type].colorId}
                                  onClick={() => {
                                    addPanelHistory({ iri: type, messageId });
                                  }}
                                  size="sm"
                                  testId={testIds.panel.dataSources.type(
                                    type,
                                    graphIdx,
                                    typeIdx
                                  )}
                                  variant="light"
                                >
                                  {getLabelOrLocalName(type)}
                                </InteractiveBadge>
                              ))}
                          </div>
                        </td>
                      </React.Fragment>
                    ))}
                  header={
                    <>
                      <td>{copy.panel.dataSources.local.graphs}</td>
                      <td>{copy.panel.dataSources.local.types}</td>
                    </>
                  }
                  size="md"
                />
                {localPages > 1 ? (
                  <TableFooterStyle4
                    onPage={setPage}
                    page={page}
                    pages={localPages}
                    size="md"
                  />
                ) : null}
              </div>
            ) : null}
            {selectedDataSource ? (
              <div className="vb-data-table">
                <TableBody
                  body={Object.entries(selectedDataSource[1])
                    .slice(ROWS_PER_PAGE * (page - 1), ROWS_PER_PAGE * page)
                    .map(([tableIri, types]) => (
                      <React.Fragment key={tableIri}>
                        <td>
                          {metadata.tablesByIri[tableIri]?.name ||
                            /* istanbul ignore next */ getLocalName(tableIri)}
                        </td>
                        <td>
                          <div className="vb-voicebox-panel-data-sources-types">
                            {Array.from(types)
                              .filter((type) => model.classesByIri[type])
                              .map((type, typeIdx) => (
                                <InteractiveBadge
                                  key={`${tableIri}-${type}`}
                                  colorId={model.classesByIri[type].colorId}
                                  onClick={() => {
                                    addPanelHistory({ iri: type, messageId });
                                  }}
                                  size="sm"
                                  testId={testIds.panel.dataSources.type(
                                    type,
                                    typeIdx
                                  )}
                                  variant="light"
                                >
                                  {getLabelOrLocalName(type)}
                                </InteractiveBadge>
                              ))}
                          </div>
                        </td>
                      </React.Fragment>
                    ))}
                  header={
                    <>
                      <td>Table Name</td>
                      <td>Stardog Concepts</td>
                    </>
                  }
                  size="md"
                />
                {virtualPages > 1 ? (
                  <TableFooterStyle4
                    onPage={setPage}
                    page={page}
                    pages={virtualPages}
                    size="md"
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </BlueprintCollapse>
  );
};
